import { useEffect, useMemo } from 'react';

import { styled, Table as MuiTable } from '@mui/material';

import { useCascadeRef } from '../../utils';
import { TableContainer } from '../TableContainer';
import { tableClasses } from './theme';

import type { TableOwnerState, TableProps } from './types';

const Table = (props: TableProps) => {
  const {
    stickyHeader = false,
    variant,
    maxHeight: maxHeightProps,
    tableContainerProps,
    showSideMargin,
    ...others
  } = props;

  const ownerState: TableOwnerState = props;

  const maxHeight = useMemo(() => {
    if (!stickyHeader) return maxHeightProps ?? '100%';

    return maxHeightProps;
  }, [maxHeightProps, stickyHeader]);

  const tableContainerRef = useCascadeRef<HTMLDivElement | null>(null, tableContainerProps?.ref);

  useEffect(() => {
    const scrollBox = tableContainerRef.current;
    const scrollBoxContents = Array.from(scrollBox?.children ?? []).filter(
      element => element instanceof HTMLElement,
    );

    if (!scrollBox || !scrollBoxContents.length) return;

    // 1. 스크롤 위치 판단 로직을 별도 함수로 분리
    let isTicking = false;
    function checkScrollPosition() {
      if (!isTicking) {
        window.requestAnimationFrame(() => {
          if (!scrollBox) return;
          const scrollLeft = scrollBox.scrollLeft;
          const clientWidth = scrollBox.clientWidth;
          const scrollWidth = scrollBox.scrollWidth;

          const isScrollStart = scrollLeft === 0 ? 'true' : 'false';
          const isScrollEnd = scrollLeft + clientWidth >= scrollWidth ? 'true' : 'false';

          scrollBox.dataset.isScrollStart = isScrollStart;
          scrollBox.dataset.isScrollEnd = isScrollEnd;
          isTicking = false;
        });
        isTicking = true;
      }
    }

    // 2. 스크롤 이벤트 리스너 등록
    scrollBox.addEventListener('scroll', checkScrollPosition);

    // 3. 리사이즈 감지 (ResizeObserver)
    // 브라우저 크기가 변해서 요소 크기가 바뀌면 실행됨
    const resizeObserver = new ResizeObserver(() => checkScrollPosition());

    // 요소 관찰 시작
    resizeObserver.observe(scrollBox); // 브라우저 또는 부모 요소의 크기 변경으로 인해  테이블 영역 크기가 변경되는지 감지
    scrollBoxContents.forEach(element => resizeObserver.observe(element)); // 컬럼 너비가 변경되면 컨텐츠(table)의 너비가 변경되는지 감지

    return () => {
      scrollBox.removeEventListener('scroll', checkScrollPosition);
      resizeObserver.disconnect();
    };
  }, [tableContainerRef]);

  return (
    <TableContainer
      variant={variant}
      {...tableContainerProps}
      ref={tableContainerRef}
      sx={{ maxHeight, ...(tableContainerProps?.sx ?? {}) }}
    >
      <TableRoot
        stickyHeader={stickyHeader}
        data-show-side-margin={showSideMargin}
        ownerState={ownerState}
        {...others}
      />
    </TableContainer>
  );
};

const TableRoot = styled(MuiTable, {
  target: tableClasses.root,
})<{ ownerState: TableOwnerState }>`
  border-collapse: separate;
`;

export default Table;
